import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { streamingServiceApi } from "@/services/streaming-service";
import {
  streamingAccountService,
  CreateStreamingAccountRequest,
  EStreamingAccountStatus,
  EStreamingAccountType,
} from "@/services/streaming-account";
import { EownerType } from "@/services/subscription";
import { EPaymentMethod, EPaymentStatus } from "@/services/payment";
import { Supplier } from "@/services/supplier";
import { SupplierCombobox } from "@/components/shared/SupplierCombobox";
import { Loader2, Truck, CalendarDays, AlertTriangle } from "lucide-react";

interface SubscriptionFormData {
  startDate: string;
  paymentMethod: EPaymentMethod;
  paymentStatus: EPaymentStatus;
  pricePlanId: string;
}

const DEFAULT_SUBSCRIPTION_DATA: SubscriptionFormData = {
  startDate: new Date().toISOString().split("T")[0],
  paymentMethod: EPaymentMethod.CASH,
  paymentStatus: EPaymentStatus.PENDING,
  pricePlanId: "",
};

interface RegisterStreamingAccountModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultServiceId?: number;
}

export function RegisterStreamingAccountModal({
  open,
  onOpenChange,
  defaultServiceId,
}: RegisterStreamingAccountModalProps) {
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<
    Omit<CreateStreamingAccountRequest, "pricePlanId" | "startDate" | "paymentMethod" | "paymentStatus" | "supplierId">
  >({
    serviceId: defaultServiceId,
    email: "",
    password: "",
    status: EStreamingAccountStatus.ACTIVE,
    type: EStreamingAccountType.SHARED,
    totalSlots: 1,
  });

  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionFormData>(
    DEFAULT_SUBSCRIPTION_DATA,
  );

  useEffect(() => {
    if (open) {
      if (defaultServiceId) {
        setFormData((prev) => ({ ...prev, serviceId: defaultServiceId }));
      }
    }
    if (!open) {
      setSelectedSupplier(null);
      setSubscriptionData(DEFAULT_SUBSCRIPTION_DATA);
    }
  }, [open, defaultServiceId]);

  // Reset el plan de precios elegido si cambia el proveedor
  useEffect(() => {
    setSubscriptionData((prev) => ({ ...prev, pricePlanId: "" }));
  }, [selectedSupplier]);

  const { data: servicesResponse } = useQuery({
    queryKey: ["streaming-services"],
    queryFn: () => streamingServiceApi.getAll(),
  });
  const streamingServices = servicesResponse?.data || [];

  // Derived: relación del proveedor seleccionado con el servicio elegido
  const selectedSupplierService = selectedSupplier?.supplierServices?.find(
    (ss) => Number(ss.serviceId) === Number(formData.serviceId),
  );
  const supplierHandlesService = !!selectedSupplierService;

  // Derived: price plans FULL_ACCOUNT del proveedor seleccionado para ese servicio
  const fullAccountPlans =
    selectedSupplierService?.service?.pricePlans?.filter(
      (p) =>
        p.pricePlanType === "FULL_ACCOUNT" &&
        p.status === "ACTIVE" &&
        p.ownerType !== EownerType.CLIENT &&
        p.supplierId === selectedSupplier?.id,
    ) || [];

  // Mutation: create streaming account (crea también la suscripción del proveedor de forma atómica)
  const registerMutation = useMutation({
    mutationFn: (data: CreateStreamingAccountRequest) =>
      streamingAccountService.register(data),
    onSuccess: () => {
      toast.success("Cuenta y suscripción del proveedor registradas exitosamente");
      queryClient.invalidateQueries({ queryKey: ["streaming-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
      handleClose();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleClose = () => {
    setFormData({
      serviceId: defaultServiceId,
      email: "",
      password: "",
      status: EStreamingAccountStatus.ACTIVE,
      type: EStreamingAccountType.SHARED,
      totalSlots: 1,
    });
    setSelectedSupplier(null);
    setSubscriptionData(DEFAULT_SUBSCRIPTION_DATA);
    onOpenChange(false);
  };

  const isPending = registerMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.serviceId) {
      toast.error("Selecciona un servicio de streaming");
      return;
    }
    if (!formData.email) {
      toast.error("Ingresa el email de la cuenta");
      return;
    }
    if (!formData.totalSlots || formData.totalSlots < 1) {
      toast.error("La cantidad de slots debe ser al menos 1");
      return;
    }
    if (!selectedSupplier) {
      toast.error("Selecciona un proveedor");
      return;
    }
    if (!supplierHandlesService) {
      toast.error("El proveedor seleccionado no maneja el servicio elegido");
      return;
    }
    if (!subscriptionData.pricePlanId) {
      toast.error("Selecciona un plan de precios para la suscripción del proveedor");
      return;
    }
    if (!subscriptionData.startDate) {
      toast.error("Ingresa la fecha de inicio de la suscripción");
      return;
    }
    if (!subscriptionData.paymentMethod) {
      toast.error("Selecciona el método de pago de la suscripción");
      return;
    }
    if (!subscriptionData.paymentStatus) {
      toast.error("Selecciona el estado de pago de la suscripción");
      return;
    }

    const payload: CreateStreamingAccountRequest = {
      ...formData,
      serviceId: Number(formData.serviceId),
      supplierId: selectedSupplier.id,
      pricePlanId: Number(subscriptionData.pricePlanId),
      startDate: subscriptionData.startDate,
      paymentMethod: subscriptionData.paymentMethod,
      paymentStatus: subscriptionData.paymentStatus,
    };

    registerMutation.mutate(payload);
  };

  const handleInputChange = (
    field: keyof typeof formData,
    value: string | number,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Registrar Cuenta de Streaming</DialogTitle>
          <DialogDescription>
            Agrega una nueva cuenta de streaming a tu inventario.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* ── Datos de la cuenta ── */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="serviceId">Servicio de Streaming *</Label>
              <Select
                value={String(formData.serviceId ?? "")}
                onValueChange={(value) => {
                  handleInputChange("serviceId", value);
                  // Reset price plan if service changes
                  setSubscriptionData((prev) => ({ ...prev, pricePlanId: "" }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un servicio" />
                </SelectTrigger>
                <SelectContent>
                  {streamingServices.map((service) => (
                    <SelectItem key={service.id} value={String(service.id)}>
                      <span className="flex items-center gap-2">
                        <span>{service.icon}</span>
                        {service.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email o usuario *</Label>
              <Input
                id="email"
                type="text"
                placeholder="cuenta@ejemplo.com"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Contraseña (opcional)</Label>
              <Input
                id="password"
                type="password"
                placeholder="Contraseña de la cuenta"
                value={formData.password || ""}
                onChange={(e) => handleInputChange("password", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Tipo de cuenta *</Label>
              <Select
                value={formData.type}
                onValueChange={(value) =>
                  handleInputChange("type", value as EStreamingAccountType)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona el tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EStreamingAccountType.SHARED}>
                    Compartida (Varios clientes)
                  </SelectItem>
                  <SelectItem value={EStreamingAccountType.DEDICATED}>
                    Dedicada (Un solo cliente)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="totalSlots">Cantidad de slots/perfiles *</Label>
              <Input
                id="totalSlots"
                type="number"
                min={1}
                max={10}
                value={formData.totalSlots ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  handleInputChange(
                    "totalSlots",
                    value === "" ? undefined : parseInt(value, 10),
                  );
                }}
                onBlur={() => {
                  if (!formData.totalSlots || formData.totalSlots < 1) {
                    handleInputChange("totalSlots", 1);
                  }
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Estado</Label>
              <Select
                value={formData.status}
                onValueChange={(value) =>
                  handleInputChange("status", value as EStreamingAccountStatus)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona el estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EStreamingAccountStatus.ACTIVE}>
                    Activa
                  </SelectItem>
                  <SelectItem value={EStreamingAccountStatus.INACTIVE}>
                    Inactiva
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ── Proveedor ── */}
          <Separator />

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Truck className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">
                Proveedor
              </span>
            </div>

            <div className="space-y-2">
              <Label htmlFor="supplierId">Proveedor *</Label>
              <SupplierCombobox
                value={selectedSupplier}
                onChange={setSelectedSupplier}
                disabled={isPending}
              />
            </div>

            {selectedSupplier && formData.serviceId && !supplierHandlesService && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {selectedSupplier.name} no maneja el servicio seleccionado.
                  Elige otro proveedor o selecciona un servicio que sí ofrezca.
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* ── Suscripción del proveedor (obligatoria, se crea junto con la cuenta) ── */}
          <Separator />

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">
                Datos de la Suscripción
              </span>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              Se crea automáticamente junto con la cuenta: es la suscripción que se
              paga al proveedor.
            </p>

            <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pricePlanId">Plan de precios *</Label>
                <Select
                  value={subscriptionData.pricePlanId}
                  onValueChange={(val) =>
                    setSubscriptionData((prev) => ({
                      ...prev,
                      pricePlanId: val,
                    }))
                  }
                  disabled={!formData.serviceId || !selectedSupplier || !supplierHandlesService}
                >
                  <SelectTrigger id="pricePlanId">
                    <SelectValue
                      placeholder={
                        !formData.serviceId
                          ? "Selecciona primero un servicio"
                          : !selectedSupplier
                            ? "Selecciona primero un proveedor"
                            : !supplierHandlesService
                              ? "El proveedor no maneja este servicio"
                              : "Selecciona un plan"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {fullAccountPlans.length === 0 ? (
                      <SelectItem value="none" disabled>
                        No hay planes de cuenta completa de este proveedor para el servicio
                      </SelectItem>
                    ) : (
                      fullAccountPlans.map((plan) => (
                        <SelectItem key={plan.id} value={String(plan.id)}>
                          {plan.name} — ${plan.price} / {plan.duration} días
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="startDate">Fecha de inicio *</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={subscriptionData.startDate}
                  onChange={(e) =>
                    setSubscriptionData((prev) => ({
                      ...prev,
                      startDate: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="paymentMethod">Método de pago *</Label>
                  <Select
                    value={subscriptionData.paymentMethod}
                    onValueChange={(val) =>
                      setSubscriptionData((prev) => ({
                        ...prev,
                        paymentMethod: val as EPaymentMethod,
                      }))
                    }
                  >
                    <SelectTrigger id="paymentMethod">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={EPaymentMethod.CASH}>Efectivo</SelectItem>
                      <SelectItem value={EPaymentMethod.TRANSFER}>Transferencia</SelectItem>
                      <SelectItem value={EPaymentMethod.CARD}>Tarjeta</SelectItem>
                      <SelectItem value={EPaymentMethod.NEQUI}>Nequi</SelectItem>
                      <SelectItem value={EPaymentMethod.DAVIPLATA}>Daviplata</SelectItem>
                      <SelectItem value={EPaymentMethod.BANCOLOMBIA}>Bancolombia</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="paymentStatus">Estado del pago *</Label>
                  <Select
                    value={subscriptionData.paymentStatus}
                    onValueChange={(val) =>
                      setSubscriptionData((prev) => ({
                        ...prev,
                        paymentStatus: val as EPaymentStatus,
                      }))
                    }
                  >
                    <SelectTrigger id="paymentStatus">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={EPaymentStatus.PENDING}>Pendiente</SelectItem>
                      <SelectItem value={EPaymentStatus.COMPLETED}>Completado</SelectItem>
                      <SelectItem value={EPaymentStatus.CANCELLED}>Cancelado</SelectItem>
                      <SelectItem value={EPaymentStatus.REFUNDED}>Reembolsado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isPending ? "Registrando..." : "Registrar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
